/**
 * Install-prompt eligibility store (task 5.3b).
 *
 * Owns the two questions the install prompt has to answer before it is
 * allowed on screen:
 *
 *   1. Has this person got something out of the app yet?
 *   2. Have they already said no?
 *
 * Both answers have to outlive the tab, so they live in localStorage. The
 * per-session guard that stops one visit being counted several times lives
 * in sessionStorage, because "a visit" is exactly a browser session.
 *
 * Storage is always reached through the try/catch helpers below: private
 * mode and blocked storage must degrade to "not eligible" rather than throw
 * on a code path that only exists to show a banner.
 */

/** localStorage: how many qualifying visits this device has made. */
export const INSTALL_VISITS_KEY = 'installPromptVisits';

/** localStorage: epoch ms before which the prompt must stay hidden. */
export const INSTALL_DISMISSED_UNTIL_KEY = 'installPromptDismissedUntil';

/** localStorage: set once the browser tells us the app was installed. */
export const INSTALL_INSTALLED_KEY = 'installPromptInstalled';

/** sessionStorage: this session's visit has already been counted. */
export const INSTALL_VISIT_SESSION_KEY = 'installPromptVisitCounted';

/**
 * Visits required before the prompt may appear.
 *
 * 2, not 1. See the trigger note in `hooks/useInstallPrompt.js` for why the
 * bar is "came back", not "arrived".
 */
export const VISITS_BEFORE_PROMPT = 2;

/**
 * How long a dismissal is honoured: 30 days.
 *
 * Long enough that saying no means no for a whole reporting period rather
 * than until tomorrow, short enough that a parent who changes their mind
 * partway through a term is offered it again without hunting through
 * browser menus. A second dismissal buys another 30 days from that moment,
 * so repeated noes keep pushing the prompt further out.
 */
export const DISMISS_SUPPRESSION_MS = 30 * 24 * 60 * 60 * 1000;

function readLocal(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Blocked storage. The prompt simply never becomes eligible.
  }
}

/**
 * Qualifying visits recorded so far.
 *
 * @returns {number} zero when never recorded or when storage is unreadable
 */
export function getInstallVisitCount() {
  const parsed = Number(readLocal(INSTALL_VISITS_KEY));
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
}

/**
 * Count this session as one qualifying visit, at most once.
 *
 * Callers are expected to have already decided that the visit qualifies --
 * that the app is showing this person their own real data. This function
 * only enforces "once per session", so a re-render, a route change or a
 * refetch cannot inflate the count into eligibility the user never earned.
 *
 * @returns {number} the visit count after this call
 */
export function recordQualifyingVisit() {
  let alreadyCounted = false;
  try {
    alreadyCounted = window.sessionStorage.getItem(INSTALL_VISIT_SESSION_KEY) === 'true';
  } catch {
    // Unreadable session storage would let every render count as a visit,
    // which is precisely the inflation this guard exists to prevent. Refuse
    // to count rather than over-count.
    return getInstallVisitCount();
  }

  if (alreadyCounted) return getInstallVisitCount();

  const next = getInstallVisitCount() + 1;
  writeLocal(INSTALL_VISITS_KEY, String(next));
  try {
    window.sessionStorage.setItem(INSTALL_VISIT_SESSION_KEY, 'true');
  } catch {
    // See above.
  }
  return next;
}

/** Whether the browser has told us the app is already installed. */
export function isInstalled() {
  return readLocal(INSTALL_INSTALLED_KEY) === 'true';
}

/** Remember an `appinstalled` event, so the prompt never returns. */
export function markInstalled() {
  writeLocal(INSTALL_INSTALLED_KEY, 'true');
}

/**
 * Whether a previous "no" is still in force.
 *
 * @param {number} [now] epoch milliseconds
 */
export function isInstallPromptSuppressed(now = Date.now()) {
  if (isInstalled()) return true;
  const until = Number(readLocal(INSTALL_DISMISSED_UNTIL_KEY));
  return Number.isFinite(until) && until > now;
}

/**
 * Record a dismissal, hiding the prompt for {@link DISMISS_SUPPRESSION_MS}.
 *
 * @param {number} [now] epoch milliseconds
 * @returns {number} the epoch ms the suppression runs until
 */
export function suppressInstallPrompt(now = Date.now()) {
  const until = now + DISMISS_SUPPRESSION_MS;
  writeLocal(INSTALL_DISMISSED_UNTIL_KEY, String(until));
  return until;
}
